import { useState, useCallback, useEffect, useRef } from 'react';
import {
  PlayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  CodeBracketIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { useEndpoints } from '@/hooks/useEndpoints';
import { useInvokeEndpoint, isAsyncResponse } from '@/hooks/useEndpointRunner';
import { useCurrentDeployments, useEnvironments } from '@/hooks/useEnvironments';
import { useResourceVersionStatus, useVersioningEnabled } from '@/hooks/useResourceVersions';
import { useFlag } from '@/hooks/useFeatureFlags';
import { NOTHING_RELEASED, RunTargetNote, runTargetState } from '@/components/endpoints/tests/RunOptionsControls';
import { FormSelect } from '@/components/shared/form/FormSelect';
import { FormTextArea } from '@/components/shared/form/FormTextArea';
import { DynamicSchemaForm } from './DynamicSchemaForm';
import AsyncPollingStatus from './AsyncPollingStatus';
import { useToast } from '@/contexts/ToastContext';
import { trackEvent } from '@/lib/analytics';
import { isSchemaRenderable, getDefaultValues, convertFormValues } from '@/utils/schemaUtils';
import { useActiveCustomDomainHostnames } from '@/hooks/useCustomDomains';
import { customDomainUrl, endpointDisplayPath, environmentDisplayPath } from '@/utils/endpointUrl';
import { endpointDisplayName } from '@/utils/endpointLabel';
import { withStopLabel } from '@/components/services/ServiceStopChip';
import { friendlyApiErrorMessage } from '@/utils/composerErrors';
import { useRequiresPlan } from '@/hooks/useRequiresPlan';
import PlanRequiredNotice from '@/components/ui/PlanRequiredNotice';
import type { Endpoint, AsyncEndpointResponse, Collaboration } from '@/types';

type InputViewMode = 'form' | 'json';

/**
 * Build an empty input template from schema property names.
 * Used as the JSON fallback when schema is not form-renderable.
 */
function buildSchemaTemplate(schema: Record<string, unknown>): string {
  const properties = schema.properties as Record<string, unknown> | undefined;
  if (properties && Object.keys(properties).length > 0) {
    const template = Object.fromEntries(
      Object.keys(properties).map(k => [k, ''])
    );
    return JSON.stringify(template, null, 2);
  }
  return '{\n  \n}';
}

// Common message field names to look for when extracting chat-friendly content
const MESSAGE_FIELDS = ['response', 'message', 'content', 'text', 'reply', 'answer'];

/**
 * Extract a chat-friendly display string from an object.
 * Prioritizes nested 'output' content for response objects, then looks for common message fields.
 */
function extractChatContent(obj: Record<string, unknown>): { text: string; isExtracted: boolean } {
  if (!obj || typeof obj !== 'object') {
    return { text: String(obj ?? ''), isExtracted: false };
  }

  // First, check for 'output' field - this contains the actual AI response in collaboration responses
  if ('output' in obj && obj.output != null && typeof obj.output === 'object') {
    const outputContent = extractChatContent(obj.output as Record<string, unknown>);
    if (outputContent.isExtracted) {
      return outputContent;
    }
  }

  // Look for common message fields
  for (const field of MESSAGE_FIELDS) {
    if (field in obj && obj[field] != null) {
      const value = obj[field];
      if (typeof value === 'string') {
        return { text: value, isExtracted: true };
      }
      // Handle nested object with message field
      if (typeof value === 'object' && value !== null) {
        const nested = extractChatContent(value as Record<string, unknown>);
        if (nested.isExtracted) {
          return nested;
        }
      }
    }
  }

  // Check for a single string field
  const entries = Object.entries(obj).filter(([, v]) => v != null);
  if (entries.length === 1 && typeof entries[0][1] === 'string') {
    return { text: entries[0][1] as string, isExtracted: true };
  }

  // Fallback: summarize the object
  const fieldCount = Object.keys(obj).length;
  return { text: `Request with ${fieldCount} field${fieldCount === 1 ? '' : 's'}`, isExtracted: false };
}

interface ExecutionResult {
  type: 'sync' | 'async';
  success: boolean;
  data: unknown;
  error?: string;
  timestamp: string;
  // For async results
  requestId?: string;
  endpointId?: string;
  pollingUrl?: string;
  // The produced collaboration, when known (sync responses surface it
  // immediately; async runs surface it once polling completes).
  collaborationId?: string;
}

interface ConversationTurn {
  input: Record<string, unknown>;
  output: Record<string, unknown>;
  timestamp: Date;
}

interface EndpointRunnerProps {
  onExecutionComplete?: (result: ExecutionResult) => void;
  initialEndpointId?: string;
  lockedEndpointId?: string;
  /**
   * Seed the request input on mount (task 365 "load into runner"). The parent
   * remounts the runner via `key` when loading a saved test, so this is
   * consumed by the initialization effect like the schema defaults would be.
   */
  initialInput?: Record<string, unknown>;
  /**
   * Render extra actions beside the Execute button (task 365 "Save as test").
   * Receives an accessor for the current parsed input (form or JSON mode);
   * returns null when the JSON body is invalid.
   */
  renderRunActions?: (getCurrentInput: () => Record<string, unknown> | null) => React.ReactNode;
}

export default function EndpointRunner({ onExecutionComplete, initialEndpointId, lockedEndpointId, initialInput, renderRunActions }: EndpointRunnerProps) {
  const effectiveInitialId = lockedEndpointId || initialEndpointId || '';
  const [selectedEndpointId, setSelectedEndpointId] = useState(effectiveInitialId);
  const [inputViewMode, setInputViewMode] = useState<InputViewMode>('form');
  const [requestBody, setRequestBody] = useState('{\n  \n}');
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [bodyError, setBodyError] = useState<string | undefined>();
  const [result, setResult] = useState<ExecutionResult | null>(null);
  const [asyncComplete, setAsyncComplete] = useState(false);
  const [collaborationId, setCollaborationId] = useState<string | null>(null);
  const [conversationHistory, setConversationHistory] = useState<ConversationTurn[]>([]);
  const [historyExpanded, setHistoryExpanded] = useState(true);
  const [expandedTurns, setExpandedTurns] = useState<Set<number>>(new Set());
  const lastInputRef = useRef<Record<string, unknown> | null>(null);

  const { data: endpointsData, isLoading: endpointsLoading } = useEndpoints(1, 100);
  const invokeEndpoint = useInvokeEndpoint();
  const toast = useToast();
  // Pre-flight no-plan gate (task 467): invoking an endpoint fails closed
  // server-side without a plan, so Execute is disabled with a reason.
  const requiresPlan = useRequiresPlan();

  const endpoints = endpointsData?.data || [];
  const selectedEndpoint = endpoints.find((e: Endpoint) => e.id === selectedEndpointId);

  /**
   * Which deployment environment this run exercises (task 760). Empty string is
   * the working state — the default, and the only option while the account has
   * no stages to address.
   *
   * WHAT DECIDES THAT IS THE ACCOUNT'S PUBLISHING SETTING (task 853), not a
   * per-service serving mode: publishing on means the account's environments
   * serve released versions of this AI Service, publishing off means it serves
   * its working state and there is nothing else to run against. There is no
   * per-service serving mode on the endpoint row to read — the field is gone
   * from the service (task 869).
   */
  const [selectedEnvironment, setSelectedEnvironment] = useState('');
  const { enabled: publishingEnabled } = useVersioningEnabled();
  const { environments } = useEnvironments({ enabled: publishingEnabled });
  /**
   * THE THREE-STATE RULE (task 1236), the Testing tab's: before adoption and
   * before the first publish the working state is the only target, so there
   * is a note instead of a selector; once published, only stages with a
   * release are offered, and the working state is not (a published service
   * never serves it). The VALUE is still the environment's path, with '' for
   * the bare URL — which is what the `/` stage (or, with no `/` stage, the
   * newest published version) serves.
   */
  const versioningFlag = useFlag('innovate.fe.resource-versioning');
  const { status: targetVersionStatus } = useResourceVersionStatus('endpoint', selectedEndpointId || undefined);
  const { data: targetDeployments } = useCurrentDeployments(selectedEndpointId || undefined, {
    enabled: publishingEnabled,
  });
  // Held back while the deployments load, so "nothing is released" never flashes.
  const targetState = versioningFlag && !(publishingEnabled && selectedEndpointId && targetDeployments === undefined)
    ? runTargetState({
        versioningEnabled: publishingEnabled,
        publishedVersion: targetVersionStatus.publishedVersion,
        environments,
        deployments: targetDeployments ?? [],
      })
    : null;
  const targetChoices = targetState?.kind === 'published' ? targetState.choices : [];
  const pathOf = (choice: (typeof targetChoices)[number]) =>
    choice.environment && choice.environment.path !== '/' ? choice.environment.path : '';
  const nothingReleased = targetState?.kind === 'published' && targetChoices.length === 0;
  const showEnvironmentSelector = targetChoices.length > 0;
  // Land on the default target when the current pick is not offered.
  useEffect(() => {
    if (!showEnvironmentSelector || targetState?.kind !== 'published') return;
    const values = targetChoices.map(pathOf);
    if (!values.includes(selectedEnvironment)) {
      setSelectedEnvironment(targetState.defaultChoice ? pathOf(targetState.defaultChoice) : values[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEnvironmentSelector, targetChoices.length, selectedEnvironment]);

  /**
   * THE CUSTOM DOMAIN IS SHOWN HERE, NEVER RUN AGAINST (task 857).
   *
   * This is the internal test surface, so the platform URL stays the target —
   * and on this surface that is not merely a default, it is the only safe
   * option. A run from this page carries the User's platform session, and a
   * custom domain resolves wherever the customer's own DNS points it; sending a
   * platform credential to an address the customer can repoint is not something
   * a test button should do. The cross-origin call is unproven besides: nothing
   * exercises a preflight through the edge tenant, so a runnable custom-domain
   * target would be offering a button that may simply fail. The address is
   * stated because a developer reading a result wants to know what the same
   * call looks like on their own domain — which the Developer Info tab hands
   * over in copyable form.
   */
  const customDomains = useActiveCustomDomainHostnames();
  const primaryCustomDomain = customDomains[0] ?? null;

  // Check if schema can be rendered as a form
  const schema = selectedEndpoint?.input as Record<string, unknown> | null;
  const canRenderAsForm = schema && isSchemaRenderable(schema);

  // Build endpoint options for dropdown. A stopped service stays selectable;
  // its label carries the stop as a warning, never a block (task 1164).
  const endpointOptions = endpoints.map((endpoint: Endpoint) => ({
    value: endpoint.id,
    label: withStopLabel(endpointDisplayName(endpoint), endpoint.stop),
  }));

  // Sync form values with JSON when switching modes
  useEffect(() => {
    if (inputViewMode === 'json' && Object.keys(formValues).length > 0) {
      // Update JSON from form values
      const convertedValues = convertFormValues(formValues, schema);
      if (Object.keys(convertedValues).length > 0) {
        setRequestBody(JSON.stringify(convertedValues, null, 2));
      }
    }
  }, [inputViewMode, formValues, schema]);

  // Initialize endpoint when initialEndpointId/lockedEndpointId is provided and endpoints are loaded
  useEffect(() => {
    const targetId = lockedEndpointId || initialEndpointId;
    if (!targetId || endpoints.length === 0) return;
    // For initialEndpointId, only run if no endpoint is selected yet
    if (!lockedEndpointId && selectedEndpointId) return;

    const endpoint = endpoints.find((ep: Endpoint) => ep.id === targetId);
    if (endpoint) {
      setSelectedEndpointId(targetId);
      const endpointSchema = endpoint.input as Record<string, unknown> | null;

      if (initialInput) {
        // Seed from a saved test's input (load-into-runner) instead of the
        // schema defaults.
        setFormValues(initialInput);
        setRequestBody(JSON.stringify(initialInput, null, 2));
        setInputViewMode(endpointSchema && isSchemaRenderable(endpointSchema) ? 'form' : 'json');
      } else if (endpointSchema && isSchemaRenderable(endpointSchema)) {
        const defaults = getDefaultValues(endpointSchema);
        setFormValues(defaults);
        setRequestBody(Object.keys(defaults).length > 0 ? JSON.stringify(defaults, null, 2) : '{\n  \n}');
        setInputViewMode('form');
      } else if (endpointSchema) {
        setRequestBody(buildSchemaTemplate(endpointSchema));
        setFormValues({});
        setInputViewMode('json');
      } else {
        setRequestBody('{\n  \n}');
        setFormValues({});
        setInputViewMode('json');
      }
    }
  }, [lockedEndpointId, initialEndpointId, endpoints, selectedEndpointId, initialInput]);

  // Current parsed input for extra run actions (e.g. "Save as test"): the
  // form values converted per schema, or the parsed JSON body. Returns null
  // when the JSON is invalid.
  const getCurrentInput = useCallback((): Record<string, unknown> | null => {
    if (inputViewMode === 'form') {
      return convertFormValues(formValues, schema);
    }
    try {
      const parsed: unknown = requestBody.trim() ? JSON.parse(requestBody) : {};
      return typeof parsed === 'object' && parsed !== null
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }, [inputViewMode, formValues, schema, requestBody]);

  // Validate JSON body
  const validateBody = useCallback((body: string): boolean => {
    if (!body.trim()) {
      setBodyError(undefined);
      return true;
    }
    try {
      JSON.parse(body);
      setBodyError(undefined);
      return true;
    } catch {
      setBodyError('Invalid JSON format');
      return false;
    }
  }, []);

  // Handle async completion
  const handleAsyncComplete = useCallback((collaboration: Collaboration) => {
    setAsyncComplete(true);
    toast.success('Collaboration completed', 'The async request has finished processing.');
    // `endpoint_tested` for the async half of a run (task 427). Queueing is not
    // a result, so the outcome is reported here instead — which also means it
    // only lands while the browser is still open. This is the test-surface
    // event, NOT `collaboration_run_completed`: that one is the server's.
    trackEvent('endpoint_tested', {
      endpoint_id: result?.endpointId,
      success: true,
      test_kind: 'adhoc',
      mode: 'async',
    });
    if (result) {
      const updatedResult: ExecutionResult = {
        ...result,
        success: true,
        data: collaboration.output || result.data,
        collaborationId: collaboration.id || result.collaborationId,
      };
      setResult(updatedResult);
      // Re-surface the completed result so panels (e.g. the hub Test tab) can
      // link to the produced run now that the collaboration id is known.
      onExecutionComplete?.(updatedResult);
    }
    // Capture collaborationId for conversation continuation
    if (collaboration.id) {
      setCollaborationId(collaboration.id);
    }
    // Append to conversation history
    if (lastInputRef.current && collaboration.output) {
      setConversationHistory(prev => [
        ...prev,
        {
          input: lastInputRef.current!,
          output: collaboration.output as Record<string, unknown>,
          timestamp: new Date(),
        }
      ]);
      lastInputRef.current = null;
    }
    // Clear input fields after async completion
    const defaults = schema ? getDefaultValues(schema) : {};
    setFormValues(defaults);
    setRequestBody(Object.keys(defaults).length > 0 ? JSON.stringify(defaults, null, 2) : '{\n  \n}');
  }, [result, toast, schema, onExecutionComplete]);

  // Handle async error
  const handleAsyncError = useCallback((error: string) => {
    setAsyncComplete(true);
    toast.error('Collaboration failed', error);
    trackEvent('endpoint_tested', {
      endpoint_id: result?.endpointId,
      success: false,
      test_kind: 'adhoc',
      mode: 'async',
    });
    if (result) {
      const updatedResult: ExecutionResult = {
        ...result,
        success: false,
        error,
      };
      setResult(updatedResult);
    }
  }, [result, toast]);

  // Handle endpoint selection change
  const handleEndpointChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const endpointId = e.target.value;
    setSelectedEndpointId(endpointId);
    setResult(null);
    setAsyncComplete(false);
    setCollaborationId(null);
    setConversationHistory([]);

    // Set default values based on endpoint input schema
    const endpoint = endpoints.find((ep: Endpoint) => ep.id === endpointId);
    const endpointSchema = endpoint?.input as Record<string, unknown> | null;

    if (endpointSchema && isSchemaRenderable(endpointSchema)) {
      // Initialize form values from schema defaults
      const defaults = getDefaultValues(endpointSchema);
      setFormValues(defaults);
      setRequestBody(Object.keys(defaults).length > 0 ? JSON.stringify(defaults, null, 2) : '{\n  \n}');
      setInputViewMode('form');
    } else if (endpointSchema) {
      // Schema exists but not renderable - show empty template
      setRequestBody(buildSchemaTemplate(endpointSchema));
      setFormValues({});
      setInputViewMode('json');
    } else {
      setRequestBody('{\n  \n}');
      setFormValues({});
      setInputViewMode('json');
    }
  };

  // Handle body change (JSON mode)
  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setRequestBody(e.target.value);
    validateBody(e.target.value);

    // Try to sync form values from JSON
    try {
      const parsed = JSON.parse(e.target.value);
      setFormValues(parsed);
    } catch {
      // Invalid JSON, don't update form values
    }
  };

  // Handle form value changes
  const handleFormChange = (values: Record<string, unknown>) => {
    setFormValues(values);
    setBodyError(undefined);
  };

  // Toggle between form and JSON views
  const handleToggleView = () => {
    if (inputViewMode === 'form') {
      // Switching to JSON - sync form values to JSON
      const convertedValues = convertFormValues(formValues, schema);
      setRequestBody(Object.keys(convertedValues).length > 0 ? JSON.stringify(convertedValues, null, 2) : '{\n  \n}');
      setInputViewMode('json');
    } else {
      // Switching to form - try to parse JSON
      if (bodyError) return; // Don't switch if JSON is invalid
      try {
        const parsed = JSON.parse(requestBody);
        setFormValues(parsed);
        setInputViewMode('form');
      } catch {
        // Can't switch to form with invalid JSON
      }
    }
  };

  // Execute the endpoint
  const handleExecute = async () => {
    if (!selectedEndpoint) return;

    // Get body based on current mode
    let parsedBody: unknown = undefined;

    if (inputViewMode === 'form') {
      // Convert form values to proper types
      parsedBody = convertFormValues(formValues, schema);
    } else {
      // Parse JSON body
      if (!validateBody(requestBody)) return;
      if (requestBody.trim()) {
        try {
          parsedBody = JSON.parse(requestBody);
        } catch {
          setBodyError('Invalid JSON format');
          return;
        }
      }
    }

    setResult(null);
    setAsyncComplete(false);

    // Merge collaborationId into body when continuing a conversation
    const bodyToSend = collaborationId && parsedBody
      ? { ...(parsedBody as Record<string, unknown>), collaborationId }
      : parsedBody;

    // `collaboration_run_started` (task 427) — fired as the request leaves,
    // which is the only moment the frontend owns. Invoking an endpoint is the
    // sole user-driven way to start a collaboration run in this app. The new
    // run's id does not exist yet, so `collaboration_id` is present only when
    // continuing an existing conversation. The matching completed/failed
    // events belong to the server (task 429) and are NOT emitted here.
    trackEvent('collaboration_run_started', {
      endpoint_id: selectedEndpoint.id,
      collaboration_id: collaborationId ?? undefined,
      is_continuation: Boolean(collaborationId),
    });

    try {
      const response = await invokeEndpoint.mutateAsync({
        // The owning account's slug comes from the endpoint row, so the call
        // hits /api/{accountSlug}/{path} (task 385).
        accountSlug: selectedEndpoint.accountSlug,
        path: selectedEndpoint.path,
        method: selectedEndpoint.method,
        body: bodyToSend,
        // A selected environment routes through /api/{slug}/{env}/{path}
        // (task 760), so the run exercises exactly what that stage serves
        // rather than the working state. Empty means the working state.
        environment: selectedEnvironment || undefined,
      });

      // Check if async response
      if (isAsyncResponse(response)) {
        const asyncData = response as AsyncEndpointResponse;
        const executionResult: ExecutionResult = {
          type: 'async',
          success: true,
          data: asyncData.data,
          timestamp: asyncData.timestamp,
          requestId: asyncData.data.requestId,
          endpointId: selectedEndpoint.id,
          pollingUrl: asyncData.data.polling?.url,
        };
        setResult(executionResult);
        onExecutionComplete?.(executionResult);
        toast.info('Request queued', 'Polling for results...');
        // Store input for history when async completes
        lastInputRef.current = parsedBody as Record<string, unknown> | null;
      } else {
        // Sync response
        const responseData = response.data as { collaborationId?: string } | null;
        const executionResult: ExecutionResult = {
          type: 'sync',
          success: true,
          data: response.data,
          timestamp: response.timestamp,
          collaborationId: responseData?.collaborationId,
        };
        setResult(executionResult);
        onExecutionComplete?.(executionResult);
        // `endpoint_tested` for a sync run — the result is already in hand.
        trackEvent('endpoint_tested', {
          endpoint_id: selectedEndpoint.id,
          success: true,
          test_kind: 'adhoc',
          mode: 'sync',
        });
        toast.success('Request successful', `${selectedEndpoint.method} ${endpointDisplayPath(selectedEndpoint)}`);

        // Capture collaborationId for conversation continuation
        if (responseData?.collaborationId) {
          setCollaborationId(responseData.collaborationId);
        }

        // Append to conversation history
        if (parsedBody && response.data) {
          setConversationHistory(prev => [
            ...prev,
            {
              input: parsedBody as Record<string, unknown>,
              output: response.data as Record<string, unknown>,
              timestamp: new Date(),
            }
          ]);
        }

        // Clear input fields after successful execution
        const defaults = schema ? getDefaultValues(schema) : {};
        setFormValues(defaults);
        setRequestBody(Object.keys(defaults).length > 0 ? JSON.stringify(defaults, null, 2) : '{\n  \n}');
      }
    } catch (error) {
      // Known backend codes (e.g. `no_active_plan`, task 465) get friendly
      // copy instead of the raw code; the global ChoosePlanPrompt also fires
      // via the interceptor for the no-plan case.
      const errorMessage =
        friendlyApiErrorMessage(error) ??
        (error instanceof Error ? error.message : 'Unknown error occurred');
      const executionResult: ExecutionResult = {
        type: 'sync',
        success: false,
        data: null,
        error: errorMessage,
        timestamp: new Date().toISOString(),
      };
      setResult(executionResult);
      onExecutionComplete?.(executionResult);
      // The invocation itself failed (transport, auth, validation) — the test
      // ran and did not work. `error_shown` rides along automatically from the
      // toast below, so only the outcome is recorded here.
      trackEvent('endpoint_tested', {
        endpoint_id: selectedEndpoint.id,
        success: false,
        test_kind: 'adhoc',
        mode: 'sync',
      });
      toast.error('Request failed', errorMessage);
    }
  };

  const isExecuting = invokeEndpoint.isPending;
  // A published service with nothing released answers 404 on every address (task 1236).
  const canExecute = selectedEndpoint && !isExecuting && !bodyError && !requiresPlan && !nothingReleased;

  return (
    <div className="space-y-6">
      {/* Endpoint Selection / Info */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        {lockedEndpointId ? (
          /* Locked mode: read-only endpoint info, no dropdown */
          selectedEndpoint ? (
            <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <div className="flex items-center space-x-3">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  selectedEndpoint.method === 'GET' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                  selectedEndpoint.method === 'POST' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                  selectedEndpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                  selectedEndpoint.method === 'PATCH' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                  'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                }`}>
                  {selectedEndpoint.method}
                </span>
                <span className="text-sm font-mono text-gray-700 dark:text-gray-300">
                  {endpointDisplayPath(selectedEndpoint)}
                </span>
                {selectedEndpoint.responseQueued && (
                  <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                    Async
                  </span>
                )}
              </div>
              {selectedEndpoint.description && (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {selectedEndpoint.description}
                </p>
              )}
            </div>
          ) : endpointsLoading ? (
            <div className="text-gray-500 dark:text-gray-400">Loading endpoint...</div>
          ) : (
            <div className="text-red-500 dark:text-red-400">Endpoint not found.</div>
          )
        ) : (
          /* Normal mode: endpoint selection dropdown */
          <>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Select Endpoint
            </h3>

            {endpointsLoading ? (
              <div className="text-gray-500 dark:text-gray-400">Loading endpoints...</div>
            ) : endpoints.length === 0 ? (
              <div className="text-gray-500 dark:text-gray-400">
                No endpoints configured. <a href="/endpoints/new" className="text-indigo-600 dark:text-indigo-400 hover:underline">Create one</a> to get started.
              </div>
            ) : (
              <FormSelect
                label="Endpoint"
                name="endpoint"
                value={selectedEndpointId}
                onChange={handleEndpointChange}
                options={endpointOptions}
                placeholder="Select an endpoint to execute..."
              />
            )}

            {selectedEndpoint && (
              <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    selectedEndpoint.method === 'GET' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                    selectedEndpoint.method === 'POST' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                    selectedEndpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                    selectedEndpoint.method === 'PATCH' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {selectedEndpoint.method}
                  </span>
                  <span className="text-sm font-mono text-gray-700 dark:text-gray-300">
                    {endpointDisplayPath(selectedEndpoint)}
                  </span>
                  {selectedEndpoint.responseQueued && (
                    <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                      Async
                    </span>
                  )}
                </div>
                {selectedEndpoint.description && (
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {selectedEndpoint.description}
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Conversation Status Banner */}
      {collaborationId && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center justify-between">
          <div className="text-sm text-blue-800 dark:text-blue-200">
            Continuing conversation
            <span className="ml-2 font-mono text-xs opacity-70">
              {collaborationId.slice(0, 8)}...
            </span>
          </div>
          <button
            onClick={() => {
              setCollaborationId(null);
              setConversationHistory([]);
            }}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            Start New
          </button>
        </div>
      )}

      {/* Conversation History */}
      {conversationHistory.length > 0 && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
          <button
            onClick={() => setHistoryExpanded(!historyExpanded)}
            className="w-full px-3 py-2 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-t-lg"
          >
            <span>Conversation History ({conversationHistory.length} {conversationHistory.length === 1 ? 'turn' : 'turns'})</span>
            <span>{historyExpanded ? '▼' : '▶'}</span>
          </button>
          {historyExpanded && (
            <div className="max-h-64 overflow-y-auto p-3 space-y-3 border-t border-gray-200 dark:border-gray-700">
              {conversationHistory.map((turn, index) => {
                const inputContent = extractChatContent(turn.input);
                const outputContent = extractChatContent(turn.output);
                const isExpanded = expandedTurns.has(index);
                const toggleExpanded = () => {
                  setExpandedTurns(prev => {
                    const next = new Set(prev);
                    if (next.has(index)) {
                      next.delete(index);
                    } else {
                      next.add(index);
                    }
                    return next;
                  });
                };

                return (
                  <div key={index} className="space-y-2">
                    {/* User input */}
                    <div className="flex justify-end">
                      <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg px-3 py-2 max-w-[80%]">
                        <div className="text-xs text-blue-600 dark:text-blue-400 mb-1">You</div>
                        {isExpanded ? (
                          <pre className="text-sm whitespace-pre-wrap break-words text-gray-800 dark:text-gray-200">
                            {JSON.stringify(turn.input, null, 2)}
                          </pre>
                        ) : (
                          <p className="text-sm text-gray-800 dark:text-gray-200">
                            {inputContent.text}
                          </p>
                        )}
                      </div>
                    </div>
                    {/* Assistant output */}
                    <div className="flex justify-start">
                      <div className="bg-gray-100 dark:bg-gray-800 rounded-lg px-3 py-2 max-w-[80%]">
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Response</div>
                        {isExpanded ? (
                          <pre className="text-sm whitespace-pre-wrap break-words text-gray-800 dark:text-gray-200">
                            {JSON.stringify(turn.output, null, 2)}
                          </pre>
                        ) : (
                          <p className="text-sm text-gray-800 dark:text-gray-200">
                            {outputContent.text}
                          </p>
                        )}
                      </div>
                    </div>
                    {/* Expand/Collapse toggle */}
                    <div className="flex justify-center">
                      <button
                        onClick={toggleExpanded}
                        className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {isExpanded ? '▲ Show less' : '▼ Show full JSON'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Request Body */}
      {selectedEndpoint && selectedEndpoint.method !== 'GET' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Request Input
            </h3>
            {canRenderAsForm && (
              <button
                type="button"
                onClick={handleToggleView}
                disabled={inputViewMode === 'json' && !!bodyError}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {inputViewMode === 'form' ? (
                  <>
                    <CodeBracketIcon className="h-4 w-4 mr-1.5" />
                    Edit as JSON
                  </>
                ) : (
                  <>
                    <Squares2X2Icon className="h-4 w-4 mr-1.5" />
                    Use Form
                  </>
                )}
              </button>
            )}
          </div>

          {inputViewMode === 'form' && canRenderAsForm && schema ? (
            <DynamicSchemaForm
              schema={schema}
              values={formValues}
              onChange={handleFormChange}
            />
          ) : (
            <FormTextArea
              label="JSON Body"
              name="body"
              value={requestBody}
              onChange={handleBodyChange}
              error={bodyError}
              rows={10}
              placeholder='{"key": "value"}'
            />
          )}
        </div>
      )}

      {/* Which deployment environment this run exercises (task 760). Sits
          beside Execute because it changes what Execute does — the URL the run
          goes to — rather than what is being sent. */}
      {showEnvironmentSelector && (
        <div className="flex flex-wrap items-center gap-2" data-testid="runner-environment-selector">
          <label
            htmlFor="runner-environment"
            className="text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Run against
          </label>
          <select
            id="runner-environment"
            value={selectedEnvironment}
            onChange={(event) => setSelectedEnvironment(event.target.value)}
            className="rounded-md border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            {/* The VALUE is the environment's path, because that is what the
                invocation URL carries and what the resolver matches (task 830);
                '' is the bare URL, which the `/` stage serves. The TEXT is the
                three-state label (task 1236): name, path, what it serves. */}
            {targetChoices.map((choice) => (
              <option key={choice.environmentId ?? 'default'} value={pathOf(choice)}>
                {choice.label}
              </option>
            ))}
          </select>
          <span className="font-mono text-xs text-gray-500 dark:text-gray-400" data-testid="runner-environment-path">
            {selectedEndpoint
              ? environmentDisplayPath(selectedEndpoint, selectedEnvironment || null)
              : ''}
          </span>
        </div>
      )}

      {/* No selector (task 1236): say why — not adopted, not yet published, or
          nothing released. */}
      {targetState && !showEnvironmentSelector && (
        <div data-testid="runner-target-note">
          <RunTargetNote state={targetState} />
        </div>
      )}

      {/* The account's own address for this same call — stated, not targeted
          (task 857). Nothing renders for an account with no active domain. */}
      {selectedEndpoint && primaryCustomDomain && (
        <div
          data-testid="runner-custom-domain"
          className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400"
        >
          <span className="font-medium">On your custom domain this call is</span>{' '}
          <code className="break-all font-mono" data-testid="runner-custom-domain-url">
            {customDomainUrl(selectedEndpoint, primaryCustomDomain, {
              environment: selectedEnvironment || null,
            })}
          </code>
          <span className="ml-1">
            — runs from this page always go to the platform URL.
          </span>
        </div>
      )}

      {/* Execute Button */}
      <div className="flex items-center space-x-4">
        <button
          onClick={handleExecute}
          disabled={!canExecute}
          title={nothingReleased ? NOTHING_RELEASED : undefined}
          className={`inline-flex items-center px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white
            ${canExecute
              ? 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
              : 'bg-gray-400 cursor-not-allowed'
            }`}
        >
          {isExecuting ? (
            <>
              <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
              Running...
            </>
          ) : (
            <>
              <PlayIcon className="h-4 w-4 mr-2" />
              Run
            </>
          )}
        </button>
        {renderRunActions?.(getCurrentInput)}
        {requiresPlan && <PlanRequiredNotice />}
      </div>

      {/* Async Polling Status */}
      {result?.type === 'async' && result.requestId && !asyncComplete && (
        <AsyncPollingStatus
          requestId={result.requestId}
          endpointId={result.endpointId}
          onComplete={handleAsyncComplete}
          onError={handleAsyncError}
        />
      )}

      {/* Result Display */}
      {result && (result.type === 'sync' || asyncComplete) && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
              {result.success ? (
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              ) : (
                <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-2" />
              )}
              Response
              {result.type === 'async' && (
                <span className="ml-2 px-2 py-1 rounded text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  Async
                </span>
              )}
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
              <ClockIcon className="h-3 w-3 mr-1" />
              {new Date(result.timestamp).toLocaleString()}
            </span>
          </div>

          {result.error ? (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-300">{result.error}</p>
            </div>
          ) : (
            <pre className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg overflow-auto max-h-96 text-sm font-mono text-gray-800 dark:text-gray-200">
              {JSON.stringify(result.data, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
